// src/features/operational/components/assessment/ProgrammeOfAssessment.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Description as DocumentIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  FormatListNumbered as MarksIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { useAuth } from '../../../../contexts/AuthContext';
import { 
  fetchProgrammeOfAssessment, 
  createProgrammeOfAssessmentTask,
  updateProgrammeOfAssessmentTask,
  deleteProgrammeOfAssessmentTask,
  fetchSubjects,
  fetchAnnualTeachingPlans
} from '../../services/assessmentService';

const ProgrammeOfAssessment = () => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [atpItems, setAtpItems] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
      subject: '',
      grade: '',
      term: '',
      category: '',
      taskType: ''
    });
    
    // Form data
    const [formData, setFormData] = useState({
      plannedDate: '',
      term: '',
      subject: '',
      grade: '',
      topics: [],
      category: 'Formal',
      taskType: '',
      marks: '',
      weighting: ''
    });
    
    // Available topics for selected subject
    const [availableTopics, setAvailableTopics] = useState([]);
    
    useEffect(() => {
      if (currentUser) {
        loadData();
      }
    }, [currentUser]);
    
    useEffect(() => {
      filterTasks();
    }, [tasks, searchTerm, filters]);
    
    useEffect(() => {
      // When subject changes, fetch available topics
      if (formData.subject) {
        const subjectAtpItems = atpItems.filter(item => item.subject.id === formData.subject);
        setAvailableTopics(subjectAtpItems);
      } else {
        setAvailableTopics([]);
      }
    }, [formData.subject, atpItems]);
    
    const loadData = async () => {
      setLoading(true);
      try {
        const [tasksData, subjectsData, atpData] = await Promise.all([
          fetchProgrammeOfAssessment(currentUser.uid),
          fetchSubjects(currentUser.uid),
          fetchAnnualTeachingPlans(currentUser.uid)
        ]);
        
        setTasks(tasksData);
        setSubjects(subjectsData);
        setAtpItems(atpData);
      } catch (error) {
        console.error("Error loading assessment data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const filterTasks = () => {
      let filtered = [...tasks];
      
      // Apply search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(task => 
          task.title.toLowerCase().includes(searchLower) ||
          task.subject.name.toLowerCase().includes(searchLower)
        );
      }
      
      // Apply filters
      if (filters.subject) {
        filtered = filtered.filter(task => task.subject.id === filters.subject);
      }
      
      if (filters.grade) {
        filtered = filtered.filter(task => task.grade === filters.grade);
      }
      
      if (filters.term) {
        filtered = filtered.filter(task => task.term === filters.term);
      }
      
      if (filters.category) {
        filtered = filtered.filter(task => task.category === filters.category);
      }
      
      if (filters.taskType) {
        filtered = filtered.filter(task => task.taskType === filters.taskType);
      }
      
      setFilteredTasks(filtered);
    };
    
    const handleFilterChange = (event) => {
      const { name, value } = event.target;
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    };
    
    const clearFilters = () => {
      setFilters({
        subject: '',
        grade: '',
        term: '',
        category: '',
        taskType: ''
      });
      setSearchTerm('');
    };
    
    const handleOpenDialog = (task = null) => {
      if (task) {
        setIsEditing(true);
        setCurrentTask(task);
        setFormData({
          plannedDate: task.plannedDate,
          term: task.term,
          subject: task.subject.id,
          grade: task.grade,
          topics: task.topics || [],
          category: task.category,
          taskType: task.taskType,
          marks: task.marks.toString(),
          weighting: task.weighting.toString()
        });
      } else {
        setIsEditing(false);
        setCurrentTask(null);
        setFormData({
          plannedDate: '',
          term: '',
          subject: '',
          grade: '',
          topics: [],
          category: 'Formal',
          taskType: '',
          marks: '',
          weighting: ''
        });
      }
      setDialogOpen(true);
    };
    
    const handleCloseDialog = () => {
      setDialogOpen(false);
    };
    
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Handle auto-selection of grade based on subject
      if (name === 'subject') {
        const selectedSubject = subjects.find(s => s.id === value);
        if (selectedSubject) {
          setFormData(prev => ({
            ...prev,
            grade: selectedSubject.grade
          }));
        }
      }
    };
    
    const handleTopicsChange = (event, newValue) => {
      setFormData(prev => ({
        ...prev,
        topics: newValue
      }));
    };
    
    const generateTaskTitle = () => {
      if (!formData.subject || !formData.grade || !formData.taskType || formData.topics.length === 0) {
        return '';
      }
      
      const selectedSubject = subjects.find(s => s.id === formData.subject);
      const subjectName = selectedSubject ? selectedSubject.name : '';
      const topicName = formData.topics.length > 0 ? formData.topics[0].topic : '';
      
      return `${subjectName} Grade ${formData.grade} ${topicName} ${formData.taskType}`;
    };
    
    const handleSubmit = async () => {
      if (!formData.subject || !formData.plannedDate || !formData.term || 
          !formData.taskType || !formData.marks || !formData.weighting) {
        // Handle validation
        return;
      }
      
      setDialogLoading(true);
      try {
        // Generate task title
        const taskTitle = generateTaskTitle();
        
        const taskData = {
          ...formData,
          title: taskTitle,
          marks: parseInt(formData.marks),
          weighting: parseFloat(formData.weighting)
        };
        
        if (isEditing && currentTask) {
          await updateProgrammeOfAssessmentTask(currentTask.id, taskData);
        } else {
          await createProgrammeOfAssessmentTask(currentUser.uid, taskData);
        }
        await loadData();
        handleCloseDialog();
      } catch (error) {
        console.error("Error saving assessment task:", error);
      } finally {
        setDialogLoading(false);
      }
    };
    
    const handleDeleteTask = async (id) => {
      if (window.confirm("Are you sure you want to delete this task? This action cannot be undone.")) {
        try {
          await deleteProgrammeOfAssessmentTask(id);
          await loadData();
        } catch (error) {
          console.error("Error deleting assessment task:", error);
        }
      }
    };
    
    const getCategoryColor = (category) => {
      return category === 'Formal' ? '#2196f3' : '#ff9800';
    };
    
    const getTaskTypeIcon = (taskType) => {
      switch (taskType) {
        case 'Test':
          return <AssignmentIcon sx={{ color: '#f44336' }} />;
        case 'Exam':
          return <DocumentIcon sx={{ color: '#9c27b0' }} />;
        case 'Assignment':
          return <DocumentIcon sx={{ color: '#2196f3' }} />;
        case 'Project':
          return <SchoolIcon sx={{ color: '#4caf50' }} />;
        case 'Investigation':
          return <SearchIcon sx={{ color: '#ff9800' }} />;
        case 'Presentation':
          return <SchoolIcon sx={{ color: '#795548' }} />;
        case 'Oral':
          return <SchoolIcon sx={{ color: '#607d8b' }} />;
        default:
          return <AssignmentIcon sx={{ color: '#9e9e9e' }} />;
      }
    };
    
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="medium">
            Programme of Assessment
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            Add Assessment Task
          </Button>
        </Box>
        
        {/* Search and Filters */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mb={2}>
            <TextField
              placeholder="Search by task title or subject..."
              variant="outlined"
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                minWidth: '120px',
                borderColor: THEME_COLORS.primary,
                color: THEME_COLORS.primary,
                '&:hover': {
                  borderColor: '#1e1c4e',
                  backgroundColor: 'rgba(39, 35, 97, 0.04)'
                }
              }}
            >
              Filters
            </Button>
          </Box>
          
          {showFilters && (
            <Grid container spacing={2} mb={1}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={filters.subject}
                    label="Subject"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Subjects</MenuItem>
                    {subjects.map(subject => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grade</InputLabel>
                  <Select
                    name="grade"
                    value={filters.grade}
                    label="Grade"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Grades</MenuItem>
                    <MenuItem value="R">Grade R</MenuItem>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(grade => (
                      <MenuItem key={grade} value={grade.toString()}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Term</InputLabel>
                  <Select
                    name="term"
                    value={filters.term}
                    label="Term"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Terms</MenuItem>
                    <MenuItem value="1">Term 1</MenuItem>
                    <MenuItem value="2">Term 2</MenuItem>
                    <MenuItem value="3">Term 3</MenuItem>
                    <MenuItem value="4">Term 4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={filters.category}
                    label="Category"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    <MenuItem value="Formal">Formal</MenuItem>
                    <MenuItem value="Informal">Informal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    name="taskType"
                    value={filters.taskType}
                    label="Task Type"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="Assignment">Assignment</MenuItem>
                    <MenuItem value="Investigation">Investigation</MenuItem>
                    <MenuItem value="Test">Test</MenuItem>
                    <MenuItem value="Exam">Exam</MenuItem>
                    <MenuItem value="Project">Project</MenuItem>
                    <MenuItem value="Presentation">Presentation</MenuItem>
                    <MenuItem value="Oral">Oral</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4} lg={2} display="flex" alignItems="center">
                <Button 
                  fullWidth
                  variant="text" 
                  color="primary" 
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          )}
        </Paper>
        
        {/* Assessment Tasks Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress sx={{ color: THEME_COLORS.primary }} />
          </Box>
        ) : filteredTasks.length === 0 ? (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 4, 
              borderRadius: 2, 
              textAlign: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.02)'
            }}
          >
            <Typography variant="body1" color="textSecondary" mb={2}>
              {searchTerm || Object.values(filters).some(v => v !== '') 
                ? 'No tasks match your search criteria' 
                : 'No assessment tasks found'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Add Your First Assessment Task
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, elevation: 1 }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                <TableRow>
                  <TableCell>Task Title</TableCell>
                  <TableCell>Subject & Grade</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Weighting</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.map((task) => (
                  <TableRow key={task.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getTaskTypeIcon(task.taskType)}
                        <Box ml={1}>
                          <Typography variant="body2" fontWeight="medium">{task.title}</Typography>
                          <Chip
                            label={`Term ${task.term}`}
                            size="small"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.625rem',
                              backgroundColor: 'rgba(39, 35, 97, 0.08)',
                              mt: 0.5
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{task.subject.name}</Typography>
                      <Typography variant="body2" color="textSecondary">Grade {task.grade}</Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(task.plannedDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Chip
                          label={task.category}
                          size="small"
                          sx={{ 
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            color: getCategoryColor(task.category),
                            mr: 1
                          }}
                        />
                        <Typography variant="body2">{task.taskType}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{task.marks}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{task.weighting}%</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Marks">
                        <IconButton 
                          size="small" 
                          color="primary"
                          component="a"
                          href={`#/task-marks/${task.id}`}
                        >
                          <MarksIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleOpenDialog(task)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDeleteTask(task.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Add/Edit Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            {isEditing ? 'Edit Assessment Task' : 'Add New Assessment Task'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={formData.subject}
                    label="Subject"
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">
                      <em>Select a subject</em>
                    </MenuItem>
                    {subjects.map(subject => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name} - Grade {subject.grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Grade"
                  name="grade"
                  value={formData.grade}
                  onChange={handleInputChange}
                  disabled={Boolean(formData.subject)}
                  helperText={formData.subject ? "Auto-selected from subject" : ""}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Planned Date"
                  name="plannedDate"
                  type="date"
                  value={formData.plannedDate}
                  onChange={handleInputChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Term</InputLabel>
                  <Select
                    name="term"
                    value={formData.term}
                    label="Term"
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">
                      <em>Select a term</em>
                    </MenuItem>
                    <MenuItem value="1">Term 1</MenuItem>
                    <MenuItem value="2">Term 2</MenuItem>
                    <MenuItem value="3">Term 3</MenuItem>
                    <MenuItem value="4">Term 4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={availableTopics}
                  value={formData.topics}
                  onChange={handleTopicsChange}
                  getOptionLabel={(option) => option.topic}
                  disabled={!formData.subject}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Topics"
                      placeholder="Select topics"
                      margin="normal"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.topic}
                        {...getTagProps({ index })}
                        sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                      />
                    ))
                  }
                />
                {!formData.subject && (
                  <Typography variant="caption" color="textSecondary">
                    Please select a subject first to see available topics
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    label="Category"
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="Formal">Formal</MenuItem>
                    <MenuItem value="Informal">Informal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    name="taskType"
                    value={formData.taskType}
                    label="Task Type"
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">
                      <em>Select a task type</em>
                    </MenuItem>
                    <MenuItem value="Assignment">Assignment</MenuItem>
                    <MenuItem value="Investigation">Investigation</MenuItem>
                    <MenuItem value="Test">Test</MenuItem>
                    <MenuItem value="Exam">Exam</MenuItem>
                    <MenuItem value="Project">Project</MenuItem>
                    <MenuItem value="Presentation">Presentation</MenuItem>
                    <MenuItem value="Oral">Oral</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Marks"
                  name="marks"
                  type="number"
                  value={formData.marks}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Weighting (%)"
                  name="weighting"
                  type="number"
                  value={formData.weighting}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 1, max: 100 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box mt={2} p={2} bgcolor="rgba(0, 0, 0, 0.03)" borderRadius={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Generated Task Title:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {generateTaskTitle() || 'Complete the form to generate a title'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={dialogLoading || !formData.subject || !formData.taskType || 
                        !formData.plannedDate || !formData.term || 
                        !formData.marks || !formData.weighting || 
                        formData.topics.length === 0}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              {dialogLoading ? 'Saving...' : (isEditing ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };
  
  export default ProgrammeOfAssessment;